import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, ClipboardList, Eye, Loader2, Plus, Trash2 } from 'lucide-react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import SettingsTabs from '../components/SettingsTabs'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'
import { inputClass } from '../components/Field'
import { FormPreview } from '../components/surveys/FormPreview'
import { RoundsPanel } from '../components/surveys/RoundsPanel'
import { EmailEditor, QuestionsEditor, TeamsEditor } from '../components/surveys/SurveyDesignEditors'
import type { Settings, SurveyDesign, SurveyRound, SurveyType } from '../types'

type View = 'rounds' | 'questions' | 'teams' | 'email'
type Draft = SurveyDesign & { name: string; description: string }

const draftOf = (t: SurveyType): Draft => ({
  name: t.name, description: t.description,
  partners: t.partners, departments: t.departments, questions: t.questions,
  emailSubject: t.emailSubject, emailBody: t.emailBody, partnerFallback: t.partnerFallback,
})

/** Whether each part of a survey is ready to send, in the order it is set up. */
const readiness = (d: Draft) => ({
  questions: d.questions.length > 0 && d.questions.every(q => q.text.trim() && (q.kind !== 'rating' || q.labels.every(l => l.trim()))),
  teams: d.departments.length > 0 && d.departments.every(x => x.name.trim()
    && (!x.areas?.length || (x.areas.length >= 2 && x.areas.every(a => a.name.trim() && a.partnerId)))),
  email: !!d.emailSubject.trim() && !!d.emailBody.trim(),
})

const fmtDay = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

/**
 * Settings > Surveys: the surveys sent by hand (the leadership survey and any
 * others like it), for survey admins. A survey holds the questions, the
 * departments and partner teams, and the email; its rounds are each one
 * sending of it, which keep the questions they were sent with.
 */
export default function SettingsSurveys() {
  useDocumentTitle('Surveys')
  const api = useApi()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const canManage = !!user?.canManageSurveys

  useEffect(() => {
    if (!authLoading && !canManage) navigate('/settings', { replace: true })
  }, [authLoading, canManage, navigate])

  const { data, loading } = useData(async () => {
    const [types, rounds, settings] = await Promise.all([
      api.getSurveyTypes(), api.getSurveyRounds(), api.getSettings() as Promise<Settings>,
    ])
    return { types, rounds, settings }
  }, [canManage])

  const [types, setTypes] = useState<SurveyType[]>([])
  const [rounds, setRounds] = useState<SurveyRound[]>([])
  useEffect(() => { if (data) { setTypes(data.types); setRounds(data.rounds) } }, [data])

  // ?survey=<id>&view=<tab> say what is open, so a link can open it again.
  const [params, setParams] = useSearchParams()
  const selected = types.find(t => t.id === params.get('survey')) ?? types[0]
  const [draft, setDraft] = useState<Draft | null>(null)
  const ready = draft ? readiness(draft) : null
  // Opens on the first part still to set up, or on Rounds once it is ready.
  const view = (params.get('view') as View | null)
    ?? (ready && !ready.questions ? 'questions' : ready && !ready.teams ? 'teams' : ready && !ready.email ? 'email' : 'rounds')
  const open = (survey: string, tab: View) => setParams({ survey, view: tab }, { replace: true })
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => { if (selected) { setDraft(draftOf(selected)); setError('') } }, [selected?.id, selected?.updatedAt])

  if (authLoading || !canManage || loading || !data) return <LoadingSpinner />

  const settings = data.settings
  const roundsOf = (id: string) => rounds.filter(r => r.typeId === id)
  const dirty = !!selected && !!draft && JSON.stringify(draft) !== JSON.stringify(draftOf(selected))
  const patch = (p: Partial<Draft>) => setDraft(d => (d ? { ...d, ...p } : d))

  const save = async () => {
    if (!selected || !draft) return
    setSaving(true)
    setError('')
    try {
      const saved = await api.updateSurveyType(selected.id, draft)
      setTypes(types.map(t => (t.id === saved.id ? saved : t)))
      // Drafts follow the survey, so they are read again.
      setRounds(await api.getSurveyRounds())
    } catch (e: any) {
      setError(e?.message || 'The survey could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const create = async () => {
    const created = await api.createSurveyType({
      name: newName.trim(), description: '', partners: [], departments: [], questions: [],
      emailSubject: newName.trim(), emailBody: '', partnerFallback: 'IT',
    })
    setTypes([...types, created])
    open(created.id, 'questions')
    setCreating(false)
    setNewName('')
  }

  const latest = (id: string) => roundsOf(id).filter(r => r.status !== 'draft').sort((a, b) => (b.sentAt ?? '').localeCompare(a.sentAt ?? ''))[0]

  // Set up in this order: what to ask, who is asked, the email, then send.
  const tabs: [View, string, boolean | null][] = selected && ready ? [
    ['questions', 'Questions', ready.questions],
    ['teams', 'Departments & teams', ready.teams],
    ['email', 'Email', ready.email],
    ['rounds', 'Send & rounds', null],
  ] : []

  return (
    <div className="max-w-6xl">
      <Modal open={deleting} title="Delete this survey?" message={`${selected?.name ?? ''} has no rounds, so only its questions are lost.`}
        confirmLabel="Delete" cancelLabel="Cancel" variant="danger" onCancel={() => setDeleting(false)}
        onConfirm={async () => {
          setDeleting(false)
          if (!selected) return
          await api.deleteSurveyType(selected.id)
          setTypes(types.filter(t => t.id !== selected.id))
          setParams({}, { replace: true })
        }} />

      {previewing && draft && <FormPreview title={draft.name} design={draft} onClose={() => setPreviewing(false)} />}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Configure integrations and defaults</p>
      </div>

      <SettingsTabs active="surveys" />

      <div className="grid grid-cols-[250px_1fr] gap-6 items-start">
        <aside className="space-y-2">
          {types.map(t => {
            const last = latest(t.id)
            const live = roundsOf(t.id).find(r => r.status === 'open')
            return (
              <button key={t.id} type="button" onClick={() => open(t.id, 'rounds')}
                className={`w-full text-left rounded-xl border p-3.5 transition-colors ${t.id === selected?.id ? 'bg-white border-[#00ACED] ring-2 ring-[#00ACED]/20 shadow-sm' : 'bg-white/60 border-slate-200 hover:bg-white'}`}>
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-[#005EB8] flex-shrink-0" />
                  <span className="font-semibold text-sm text-slate-800 truncate">{t.name}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1.5">
                  {live ? <span className="text-[#0077A8] font-semibold">{live.name} open</span>
                    : last ? `Last sent ${fmtDay(last.sentAt)}` : 'Not sent yet'}
                  <span className="text-slate-400"> · {roundsOf(t.id).length} rounds</span>
                </div>
              </button>
            )
          })}
          {creating ? (
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
              <input autoFocus className={inputClass} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Survey name"
                onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) create() }} />
              <div className="flex gap-2">
                <button type="button" className="btn-primary text-white px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50" disabled={!newName.trim()} onClick={create}>Create</button>
                <button type="button" className="text-sm text-slate-500 hover:text-slate-700 px-2" onClick={() => setCreating(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setCreating(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 border border-dashed border-slate-300 rounded-xl text-sm font-medium text-slate-600 hover:bg-white hover:border-slate-400">
              <Plus className="w-4 h-4" /> New survey
            </button>
          )}
        </aside>

        {selected && draft ? (
          <section className="min-w-0">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0 flex-1">
                <input className="text-xl font-bold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#00ACED] focus:outline-none w-full"
                  value={draft.name} onChange={e => patch({ name: e.target.value })} aria-label="Survey name" />
                <input className="text-sm text-slate-500 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#00ACED] focus:outline-none w-full mt-1"
                  value={draft.description} onChange={e => patch({ description: e.target.value })} placeholder="Who it's for and how often" aria-label="Description" />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={() => setPreviewing(true)}
                  className="bg-white border border-slate-200 text-slate-700 px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm">
                  <Eye className="w-4 h-4" /> Preview
                </button>
                {roundsOf(selected.id).length === 0 && (
                  <button type="button" onClick={() => setDeleting(true)} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Delete survey"><Trash2 className="w-4 h-4" /></button>
                )}
                {dirty && (
                  <>
                    <button type="button" className="text-sm text-slate-500 hover:text-slate-700 px-2" onClick={() => setDraft(draftOf(selected))}>Discard</button>
                    <button type="button" onClick={save} disabled={saving} className="btn-primary text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
                    </button>
                  </>
                )}
              </div>
            </div>

            {error && <div className="mb-4 px-3 py-2 rounded-lg text-sm border bg-red-50 border-red-200 text-red-700">{error}</div>}

            <div className="mb-5">
              <div className="inline-flex gap-1 p-1 bg-slate-100 rounded-xl whitespace-nowrap">
                {tabs.map(([id, label, done], i) => (
                  <button key={id} type="button" aria-pressed={view === id} onClick={() => open(selected.id, id)}
                    className={`pl-2 pr-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${view === id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${done
                      ? 'bg-[#00843D] text-white' : view === id ? 'bg-[#005EB8] text-white' : 'bg-slate-200 text-slate-500'}`}>
                      {done ? <Check className="w-3 h-3" /> : i + 1}
                    </span>
                    {label}
                    {id === 'rounds' && <span className="text-[11px] font-bold text-slate-400 tabular-nums">{roundsOf(selected.id).length}</span>}
                  </button>
                ))}
              </div>
              {view !== 'rounds' && <div className="text-xs text-slate-400 mt-2">Changes reach draft rounds, not sent ones.</div>}
            </div>

            {view === 'rounds' && (
              <RoundsPanel api={api} type={selected} rounds={roundsOf(selected.id)}
                onChanged={next => setRounds([...rounds.filter(r => r.typeId !== selected.id), ...next])} />
            )}
            {view === 'questions' && <QuestionsEditor questions={draft.questions} onChange={questions => patch({ questions })} />}
            {view === 'teams' && (
              <TeamsEditor design={draft} onChange={patch} settingsTeams={settings.teams ?? []}
                settingsPortfolios={(settings.portfolios ?? []).map(p => p.name)} />
            )}
            {view === 'email' && <EmailEditor design={draft} onChange={patch} />}
          </section>
        ) : (
          <div className="card p-8 text-center text-sm text-slate-500">No surveys yet. Create one to start.</div>
        )}
      </div>
    </div>
  )
}
